import React from 'react'
import { Button, Card, CardContent, CardHeader, Label } from '@/shared/ui'
import { Input } from '@/shared/ui/Input'
import { Textarea } from '@/shared/ui/Textarea'
import { useToast } from '@/shared/ui/Toast'
import { estimateRecipients, estimateEligible, createBroadcast, enqueueBroadcast, enqueueBroadcastTestEmail } from '../services/broadcastService'
import { supabase } from '@/lib/supabase'

type Plan = { id: string; name?: string | null }

const ROLE_OPTIONS = [
  { id: 'member', label: 'Membros' },
  { id: 'afiliado', label: 'Afiliados' },
  { id: 'master', label: 'Masters' },
]

export default function BroadcastManualForm() {
  const { add: toast } = useToast()
  const [plans, setPlans] = React.useState<Plan[]>([])
  const [loadingPlans, setLoadingPlans] = React.useState(true)
  const [title, setTitle] = React.useState('')
  const [body, setBody] = React.useState('')
  const [url, setUrl] = React.useState('/oportunidades')
  const [selectedPlans, setSelectedPlans] = React.useState<string[]>([])
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>(['master'])
  const [estimating, setEstimating] = React.useState(false)
  const [eligible, setEligible] = React.useState<number | null>(null)
  const [estimate, setEstimate] = React.useState<number | null>(null)
  const [sending, setSending] = React.useState(false)
  const [testEmail, setTestEmail] = React.useState('')
  const [sendingTest, setSendingTest] = React.useState(false)
  const [scheduleAt, setScheduleAt] = React.useState<string>('')

  React.useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const { data, error } = await supabase.from('plans').select('id,name').order('id', { ascending: true })
        if (!error && alive) setPlans((data || []) as any)
      } finally {
        if (alive) setLoadingPlans(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  async function onEstimate() {
    if (!selectedPlans.length && !selectedRoles.length) {
      setEligible(0)
      setEstimate(0)
      return
    }
    setEstimating(true)
    try {
      const [el, rc] = await Promise.all([estimateEligible(selectedPlans, selectedRoles), estimateRecipients(selectedPlans, selectedRoles)])
      setEligible(el)
      setEstimate(rc)
    } catch (e: any) {
      toast({ title: 'Broadcast', description: e?.message || 'Falha ao estimar', variant: 'error' })
    } finally {
      setEstimating(false)
    }
  }

  async function onSend() {
    if (!title.trim() || !body.trim() || (!selectedPlans.length && !selectedRoles.length)) {
      toast({
        title: 'Broadcast',
        description: 'Preencha título, mensagem e selecione ao menos um plano ou grupo de usuários.',
        variant: 'error',
      })
      return
    }
    setSending(true)
    try {
      const id = await createBroadcast(
        title.trim(),
        body.trim(),
        url.trim() || null,
        selectedPlans,
        selectedRoles,
        scheduleAt ? new Date(scheduleAt).toISOString() : null,
      )
      const enq = await enqueueBroadcast(id)
      toast({ title: 'Broadcast', description: `Enfileirado para ${enq} inscrições.`, variant: 'success', soft: true })
      setEstimate(null)
      setEligible(null)
    } catch (e: any) {
      toast({ title: 'Broadcast', description: e?.message || 'Falha ao enviar', variant: 'error' })
    } finally {
      setSending(false)
    }
  }

  async function onSendTest() {
    if (!testEmail.trim()) {
      toast({ title: 'Broadcast', description: 'Informe um e-mail para teste.', variant: 'error' })
      return
    }
    setSendingTest(true)
    try {
      const n = await enqueueBroadcastTestEmail(
        title.trim() || 'Cripto Alert',
        body.trim() || 'Teste de broadcast',
        url.trim() || '/oportunidades',
        testEmail.trim(),
      )
      toast({
        title: 'Broadcast (teste)',
        description: n > 0 ? 'Enfileirado.' : 'Inscrição não encontrada/ativa para este email.',
        variant: n > 0 ? 'success' : 'warning',
        soft: true,
      })
    } catch (e: any) {
      toast({ title: 'Broadcast (teste)', description: e?.message || 'Falha ao enviar teste', variant: 'error' })
    } finally {
      setSendingTest(false)
    }
  }

  const hasTargets = selectedPlans.length > 0 || selectedRoles.length > 0

  return (
    <Card>
      <CardHeader className="text-sm font-medium">Broadcast Manual (master)</CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Título</Label>
            <Input
              value={title}
              onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
              placeholder="Ex.: Recurso novo disponível"
            />
          </div>
          <div className="space-y-2">
            <Label>URL (opcional)</Label>
            <Input
              value={url}
              onChange={(e) => setUrl((e.target as HTMLInputElement).value)}
              placeholder="/oportunidades"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Mensagem</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody((e.target as HTMLTextAreaElement).value)}
              rows={3}
              placeholder="Descreva resumidamente a novidade"
            />
          </div>
          <div className="space-y-2">
            <Label>Planos</Label>
            <div className="flex flex-wrap gap-2">
              {loadingPlans ? (
                <span className="text-xs text-foreground/60">Carregando.</span>
              ) : plans.length ? (
                plans.map((p) => (
                  <label
                    key={p.id}
                    className="inline-flex items-center gap-2 rounded border border-border px-2 py-1 text-xs"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPlans.includes(p.id)}
                      onChange={(e) => {
                        const on = (e.target as HTMLInputElement).checked
                        setSelectedPlans((prev) =>
                          on ? Array.from(new Set([...prev, p.id])) : prev.filter((x) => x !== p.id),
                        )
                      }}
                    />
                    <span>{p.name || p.id}</span>
                  </label>
                ))
              ) : (
                <span className="text-xs text-foreground/60">Nenhum plano encontrado</span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="flex flex-wrap gap-2">
              {ROLE_OPTIONS.map((role) => (
                <label
                  key={role.id}
                  className="inline-flex items-center gap-2 rounded border border-border px-2 py-1 text-xs"
                >
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role.id)}
                    onChange={(e) => {
                      const on = (e.target as HTMLInputElement).checked
                      setSelectedRoles((prev) =>
                        on ? Array.from(new Set([...prev, role.id])) : prev.filter((x) => x !== role.id),
                      )
                    }}
                  />
                  <span>{role.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Agendar (opcional)</Label>
            <Input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt((e.target as HTMLInputElement).value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Estimativa</Label>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground/80">
              <Button
                variant="outline"
                onClick={onEstimate}
                disabled={!hasTargets || estimating}
              >
                {estimating ? '...' : 'Estimar'}
              </Button>
              <span>Elegíveis (assinatura/role): {eligible == null ? '-' : eligible}</span>
              <span>Com push ativo (entrega): {estimate == null ? '-' : estimate}</span>
            </div>
          </div>
          <div className="space-y-2 md:col-span-2">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div className="flex items-center gap-2">
                <Button
                  onClick={onSend}
                  disabled={sending || !hasTargets || !title.trim() || !body.trim()}
                >
                  {sending ? 'Enviando.' : 'Enviar'}
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="email para teste"
                  value={testEmail}
                  onChange={(e) => setTestEmail((e.target as HTMLInputElement).value)}
                  className="w-60"
                />
                <Button
                  variant="outline"
                  onClick={onSendTest}
                  disabled={sendingTest || !testEmail.trim()}
                >
                  {sendingTest ? 'Testando.' : 'Enviar teste'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

